import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "configureStore";
import useIsPhone from "features/files/use-is-phone";
import { usePresenceRailWidth } from "features/presence/use-presence-rail-width";
import { MiniChatBox } from "./mini-chat-box";
import { MiniChatLauncher } from "./mini-chat-launcher";

export const MiniChatContainer: React.FC = () => {
  const activeMiniChats = useSelector((state: RootState) => state.chatReducer.activeMiniChats);
  const { companyId } = useParams<{ companyId: string }>();
  const isPhone = useIsPhone();
  const railWidth = usePresenceRailWidth(companyId);

  // Exactly one floating widget: either the expanded chat, or (if all minimized)
  // a single compact bar — the most recently active minimized thread.
  const chatsForStrip = useMemo(() => {
    const expandedList = activeMiniChats.filter((c) => !c.isMinimized).slice(0, 1);
    if (expandedList.length > 0) return expandedList;
    const minimizedList = activeMiniChats
      .filter((c) => c.isMinimized)
      .sort((a, b) => b.lastActiveAt - a.lastActiveAt);
    return minimizedList.slice(0, 1);
  }, [activeMiniChats]);

  // Stops 16px short of the presence rail; a phone has no rail to clear.
  const panelOffset = isPhone ? 0 : railWidth + 16;

  return (
    <div
      data-testid="mini-chat-container"
      className="fixed bottom-[var(--phone-bar-height,0px)] flex flex-row-reverse items-end gap-4 pointer-events-none z-[400] overflow-visible transition-[right] duration-200 p-4"
      style={{ right: `${panelOffset}px` }}
    >
      {/* 1. The Social Hub Launcher (Extreme Right) */}
      <MiniChatLauncher />

      {/* 2. Single floating slot: one expanded window OR one minimized bar (never both). */}
      <div className="flex flex-row-reverse items-end gap-3 pointer-events-none">
        {chatsForStrip.map((chat) => (
          <div
            key={chat.conversationId}
            className="pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
          >
            <MiniChatBox
              companyId={chat.companyId}
              conversationId={chat.conversationId}
              isMinimized={chat.isMinimized}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
